import { memo, ReactNode, useEffect, useState } from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckOutlined, ContentCopyOutlined, Close, WarningAmberOutlined } from '@mui/icons-material';
import { LoadingButton } from '@dfl/mui-react-common';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { CodeInput } from 'components/CodeInput';
import useResendCountdown from 'modules/authentication/hooks/useResendCountdown';
import MfaService from 'modules/authentication/services/mfa.service';
import { BackupCodesResponse, MfaError, MfaMethod, MfaSetupResponse } from 'modules/authentication/services/mfa.types';

type MfaSetupDialogProps = {
  method: Extract<MfaMethod, 'TOTP' | 'EMAIL'>;
  onSuccess: (response: BackupCodesResponse) => void;
  onClose: () => void;
};

const CODE_LENGTH = 6;

/** The manual key an authenticator app accepts when the QR cannot be scanned. */
const readSecret = (otpauthUri?: string) => otpauthUri?.match(/[?&]secret=([^&]+)/i)?.[1];

const Step = ({ index, children }: { index: number; children: ReactNode }) => (
  <Stack direction='row' spacing={1.5} alignItems='flex-start'>
    <Box
      sx={{
        flex: '0 0 auto',
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 13,
        fontWeight: 600,
        color: 'primary.main',
        bgcolor: ({ palette }) => alpha(palette.primary.main, 0.12),
      }}
    >
      {index}
    </Box>
    <Box flex={1} minWidth={0}>{children}</Box>
  </Stack>
);

const MfaSetupDialog = ({ method, onSuccess, onClose }: MfaSetupDialogProps) => {
  const { t } = useTranslation('authentication');
  const [code, setCode] = useState('');
  const [setup, setSetup] = useState<MfaSetupResponse>();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string>();
  const [copied, setCopied] = useState(false);
  const countdown = useResendCountdown();
  const isTotp = method === 'TOTP';
  const secret = readSecret(setup?.otpauthUri);

  const setupMutation = useMutation<MfaSetupResponse | { sent: true }>(
    () => (isTotp ? MfaService.startTotpSetup() : MfaService.sendEmailSetup()),
    {
      onSuccess: (response) => {
        if (isTotp && 'qrDataUrl' in response) setSetup(response);
        else {
          setSent(true);
          countdown.restart();
        }
        setError(undefined);
      },
      onError: () => { setError(t('mfa.errors.generic')); },
    },
  );

  const verifyMutation = useMutation(
    () => (isTotp ? MfaService.verifyTotpSetup(code) : MfaService.verifyEmailSetup(code)),
    {
      onSuccess,
      onError: (requestError: MfaError) => {
        setError(t(requestError?.reference === 'PJA021' ? 'mfa.errors.invalidCode' : 'mfa.errors.generic'));
      },
    },
  );

  useEffect(() => {
    setupMutation.mutate();
    // This dialog owns a single setup attempt. The mutation function is selected from its immutable method prop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!copied) return undefined;
    const timeout = setTimeout(() => { setCopied(false); }, 2000);
    return () => { clearTimeout(timeout); };
  }, [copied]);

  const ready = isTotp ? !!setup : sent;
  const isComplete = code.length === CODE_LENGTH;

  const copySecret = async () => {
    if (!secret) return;
    await navigator.clipboard?.writeText(secret);
    setCopied(true);
  };

  const resend = () => {
    setCode('');
    setError(undefined);
    setupMutation.mutate();
  };

  const submit = () => {
    if (isComplete) verifyMutation.mutate();
  };

  return (
    // Closing reopens the setup and sends a new code, so only Cancel or the close icon may dismiss it.
    <Dialog open disableEscapeKeyDown maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pr: 7, fontWeight: 700 }}>
        {t(isTotp ? 'mfa.setup.totpTitle' : 'mfa.setup.emailTitle')}
        <IconButton
          aria-label={t('mfa.cancel')}
          onClick={onClose}
          sx={{ position: 'absolute', right: 12, top: 12, color: 'text.secondary' }}
        >
          <Close fontSize='small' />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && <Alert severity='error' sx={{ mb: 2.5 }}>{error}</Alert>}

        <Stack spacing={3}>
          {isTotp && (
            <Step index={1}>
              <Typography variant='body2' color='text.secondary' mb={2}>
                {t('mfa.setup.totpDescription')}
              </Typography>
              <Box sx={{ minHeight: 208, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {setup ? (
                  <Box
                    component='img'
                    src={setup.qrDataUrl}
                    alt={t('mfa.setup.qrAlt')}
                    sx={{ width: 192, height: 192, p: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}
                  />
                ) : setupMutation.isError ? (
                  <Button variant='outlined' color='inherit' onClick={resend}>{t('mfa.setup.retry')}</Button>
                ) : (
                  <CircularProgress aria-label={t('mfa.setup.qrLoading')} />
                )}
              </Box>
              {secret && (
                <Stack
                  direction='row'
                  alignItems='center'
                  spacing={1}
                  sx={{ mt: 2, px: 1.5, py: 1, borderRadius: 1.5, bgcolor: 'action.hover' }}
                >
                  <Typography
                    component='code'
                    sx={{ flex: 1, fontFamily: 'monospace', fontSize: 13, letterSpacing: 0.5, wordBreak: 'break-all' }}
                  >
                    {secret}
                  </Typography>
                  <Tooltip title={t(copied ? 'mfa.setup.secretCopied' : 'mfa.setup.copySecret')}>
                    <IconButton size='small' onClick={copySecret} aria-label={t('mfa.setup.copySecret')}>
                      {copied ? <CheckOutlined fontSize='small' color='success' /> : <ContentCopyOutlined fontSize='small' />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              )}
            </Step>
          )}

          {!isTotp && (
            <Step index={1}>
              <Typography variant='body2' color='text.secondary'>
                {t(setupMutation.isError ? 'mfa.setup.emailFailed' : sent ? 'mfa.setup.emailSent' : 'mfa.setup.emailSending')}
              </Typography>
              {(sent || setupMutation.isError) && (
                <Button
                  size='small'
                  sx={{ mt: 0.5, px: 0 }}
                  disabled={setupMutation.isLoading || (!setupMutation.isError && !countdown.canResend)}
                  onClick={resend}
                >
                  {setupMutation.isError
                    ? t('mfa.setup.retry')
                    : countdown.canResend
                      ? t('mfa.resendCode')
                      : t('mfa.resendCodeIn', { seconds: countdown.remaining })}
                </Button>
              )}
            </Step>
          )}

          <Step index={2}>
            <Typography variant='body2' color='text.secondary' mb={2}>
              {t(isTotp ? 'mfa.setup.totpCodeLabel' : 'mfa.setup.emailCodeLabel')}
            </Typography>
            <CodeInput
              value={code}
              onChange={(next) => { setCode(next); setError(undefined); }}
              disabled={!ready || verifyMutation.isLoading}
              error={!!error}
              ariaLabel={t('mfa.code')}
            />
          </Step>

          <Stack
            direction='row'
            spacing={1.5}
            sx={{ p: 1.5, borderRadius: 1.5, bgcolor: ({ palette }) => alpha(palette.warning.main, 0.12) }}
          >
            <WarningAmberOutlined fontSize='small' sx={{ color: 'warning.main', mt: '2px' }} />
            <Typography variant='body2' color='text.secondary'>
              {t('mfa.setup.backupCodesNotice')}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button color='inherit' onClick={onClose}>{t('mfa.cancel')}</Button>
        <LoadingButton
          variant='contained'
          loading={verifyMutation.isLoading}
          disabled={!ready || !isComplete}
          onClick={submit}
        >
          {t('mfa.setup.activate')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default memo(MfaSetupDialog);
